import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setUser } from "../../features/user/userSlice";
import { useState } from "react";
import Select from "react-select";
import countryList from "react-select-country-list";
import { API_BASE } from "../../config/apiConfig";
import { FiCheck, FiX } from "react-icons/fi";
import AvatarUpload from "./avatarUpload";

const EditProfilePage = () => {
  const user = useSelector((state) => state.user.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const countryOptions = countryList().getData();

  const [fullName, setFullName] = useState(user?.fullName || "");
  const [dob, setDob] = useState(user?.dob || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [gender, setGender] = useState(user?.gender || "");
  const [country, setCountry] = useState(user?.country || "");
  const [wsfSpin, setWsfSpin] = useState(user?.wsfSpin || "");
  const [wsfExpiry, setWsfExpiry] = useState(user?.wsfExpiry || "");

  const [saving, setSaving] = useState(false);

  const formatDate = (ts) =>
    ts ? new Date(ts).toLocaleDateString("en-MY") : "-";

  const hasRole = (u, roleName) => {
    if (!u?.role) return false;
    if (Array.isArray(u.role)) return u.role.includes(roleName);
    return u.role === roleName;
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!fullName.trim()) {
      alert("Full name is required.");
      return;
    }

    setSaving(true);

    try {
      const token = sessionStorage.getItem("access_token");

      const res = await fetch(`${API_BASE}/api/update-profile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ fullName, dob, phone, gender, country, wsfSpin, wsfExpiry }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message);

      // Update user locally
      dispatch(setUser(data.updated_user));
      sessionStorage.setItem("userDetail", JSON.stringify(data.updated_user));

      // Redirect back to Account Info
      navigate("/profile");
    } catch (err) {
      alert("Failed to update profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full flex-grow flex flex-col">
      <div className="w-full max-w-5xl mx-auto px-4 py-8">

        {/* Header Section */}
        <div className="flex flex-col md:flex-row items-center gap-4 mb-6">
          <AvatarUpload />
          <h2 className="text-2xl font-semibold">{user.fullName}</h2>
        </div>

        {/* Edit Form Card */}
        <div className="relative bg-white rounded-lg shadow p-6">

          {/* Action Icons */}
          <div className="absolute right-4 top-4 flex items-center gap-3">
            <button
              type="submit"
              form="edit-form"
              disabled={saving}
              className="text-green-600 hover:text-green-700"
            >
              <FiCheck size={22} />
            </button>

            <button
              className="text-red-500 hover:text-red-600"
              onClick={() => navigate("/profile")}
            >
              <FiX size={22} />
            </button>
          </div>

          {/* Reserve space for icons */}
          <div className="h-6"></div>

          <form id="edit-form" onSubmit={handleSave}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              {/* Editable Fields */}
              <EditField label="Full Name" required>
                <input
                  type="text"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                />
              </EditField>

              <EditField label="Date of Birth" optional>
                <input
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                />
              </EditField>

              <EditField label="Phone" optional>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) =>
                    setPhone(e.target.value.replace(/\D/g, ""))
                  }
                  className="w-full border rounded px-3 py-2 placeholder:italic placeholder:text-gray-400"
                  placeholder="0123456789"
                />
              </EditField>

              <EditField label="Gender" optional>
                <select
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">-</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </EditField>

              <EditField label="Country" optional>
                <Select
                  options={countryOptions}
                  value={countryOptions.find((o) => o.value === country) || null}
                  onChange={(opt) => setCountry(opt.value)}
                  classNamePrefix="react-select"
                  placeholder="Select Country"
                />
              </EditField>

              {/* Read-only fields */}
              <ReadOnlyField label="Member Since" value={formatDate(user.createdAt)} />

              {hasRole(user, "player") && (
                <>
                  <ReadOnlyField label="Subscription Plan" value={user.plan || "Free"} />

                  <ReadOnlyField
                    label="Plan Expiry"
                    value={
                      user.plan === "Free"
                        ? "-"
                        : formatDate(user.plan_expires_at)
                    }
                  />
                </>
              )}

              {/* WSF SPIN (optional) */}
              <EditField label="WSF SPIN" optional tooltip tooltipText="Squash Personal Identification Number from the World Squash Federation (WSF)">
                <input
                  type="text"
                  value={wsfSpin}
                  onChange={(e) => setWsfSpin(e.target.value)}
                  placeholder="Enter SPIN"
                  className="w-full border rounded px-3 py-2 placeholder:italic placeholder:text-gray-400"
                />
              </EditField>

              {/* SPIN Expiry (optional) */}
              <EditField label="SPIN Expiry" optional>
                <input
                  type="date"
                  value={wsfExpiry}
                  onChange={(e) => setWsfExpiry(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                />
              </EditField>

              <ReadOnlyField
                label="Role"
                value={
                  Array.isArray(user.role)
                    ? user.role.map((r) => r.charAt(0).toUpperCase() + r.slice(1)).join(", ")
                    : user.role
                    ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
                    : "-"
                }
              />

              {hasRole(user, "player") && (
                <ReadOnlyField label="Coach" value={user.coachName || "-"} />
              )}

              <ReadOnlyField label="Email" value={user.email} />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const EditField = ({ label, children, optional=false, required=false, tooltip=false, tooltipText="" }) => (
  <div className="flex flex-col">
    <span className="flex text-sm font-medium mb-1 items-center gap-1 ml-2">
      {label}
      {required && <span className="text-red-500 italic text-xs"> *Required</span>}
      
      {/* Tooltip wrapper */}
      {tooltip && (
        <div className="relative group">
          {/* Icon */}
          <div
            className="w-4 h-4 flex items-center justify-center rounded-full bg-gray-300 text-gray-700 text-xs cursor-pointer"
          >
            ?
          </div>

          {/* Tooltip on hover */}
          <div className="absolute mt-2 w-56 p-2 rounded-md text-xs text-white bg-gray-800 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
            {tooltipText}
          </div>
        </div>
      )}

      {optional && <span className="italic text-gray-400 text-xs"> (Optional)</span>}
    </span>
    <div className="mt-1">
      {children}
    </div>
  </div>
);

const ReadOnlyField = ({ label, value }) => (
  <div className="flex flex-col opacity-70 cursor-not-allowed">
    <span className="text-gray-500 text-sm font-medium">{label}</span>
    <span className="text-gray-700 font-semibold mt-0.5">{value || "-"}</span>
  </div>
);

export default EditProfilePage;
